import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { snakeCase } from "lodash";
import Table, {
  TableHeading,
  TableAction,
  TableControls,
  TableFooter,
  buildTagColumn,
  buildActionColumn,
  buildExpandableCountColumn,
  buildIconTextColumn
} from "@/components/Table";
import type { IconConfig } from "@/components/Table";
import AvatarDetails from "@/components/AvatarDetails";
import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import Select from "@/components/Select";
import { renderEmptyCellState } from "@/components/Table/utils";
import { MentionedText } from "@/components/AtMention";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { useCanManageTasksBool } from "@/hooks/useCapabilities";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import usePagination from "@/hooks/usePagination";
import { useRowHighlight } from "@/hooks/useRowHighlight";
import { toFormat } from "@/lib/dateHelpers";
import { TaskStageValues, TaskVisibilityValues, type TaskStage } from "@/types";
import { STAGE_DISPLAY, STAGE_TAG_TYPE, getOpenTaskOverdueTagExtras } from "@/pages/tasks/_utils/taskHelpers";
import { TASKS_QUERY, type TaskNode, type TasksQueryResponse, type TasksQueryVariables } from "@/pages/tasks/queries";
import useAmplitude from "@/hooks/useAmplitude";
import { TASK_ANALYTICS_EVENTS, getTaskAnalyticsData } from "@/pages/tasks/_utils/amplitude";
import TaskSideSheet from "@/features/global/components/TaskSideSheet";
import DeleteTaskDialog from "@/pages/tasks/_components/DeleteTaskDialog";
import ReferencesTable, { getReferenceParentLink } from "@/features/global/components/ReferencesTable";
import AddTaskSideSheet from "@/features/global/components/AddTaskSideSheet/AddTaskSideSheet";
import type { BackofficeCRMReferenceableInput } from "@/features/global/components/AddTaskSideSheet/mutations";
import { Link, type Path, useNavigate } from "@/router";

export interface TasksTableProps {
  referenceableId: string;
  referenceableType: string;
  tableHeading?: string;
  tableBody?: string;
  hideControls?: boolean;
  defaultPrivate?: boolean;
  skipTeamAutoTag?: boolean;
}

type TaskOpenSource = "name_link" | "row" | "view_button" | "row_action";

function buildDataColumns(
  isCompactLayout: boolean,
  commentsEnabled: boolean,
  onOpenTask?: (task: TaskNode, openSource: TaskOpenSource) => void
): ColumnDef<TaskNode, any>[] {
  const stageColumn = buildTagColumn<TaskNode>({
    accessorKey: "stage",
    header: "Stage",
    getTagLabel: (row) => STAGE_DISPLAY[row.stage] || row.stage,
    getTagType: (row) => STAGE_TAG_TYPE[row.stage] || "surface",
    getTagExtras: (row) => getOpenTaskOverdueTagExtras(row),
    size: 120,
    enableSorting: true,
    sortType: "text"
  });

  return [
    buildIconTextColumn<TaskNode>({
      accessorKey: "name",
      header: "Name",
      enableSorting: true,
      sortType: "text",
      size: 280,
      minSize: 240,
      iconPosition: "after",
      meta: { headerClassName: "hide-header-in-column" },
      getText: (row) => {
        const count = commentsEnabled ? (row.commentsCount ?? 0) : 0;
        return (
          <>
            <Link
              to="/tasks/:taskId"
              params={{ taskId: row.id }}
              onClick={(e) => {
                e.stopPropagation();
                onOpenTask?.(row, "name_link");
              }}
            >
              {row.name ? <MentionedText content={row.name} /> : "--"}
            </Link>
            {count > 0 && (
              <span className="sr-only">
                {count} {count === 1 ? "comment" : "comments"}
              </span>
            )}
          </>
        );
      },
      getIcon: (row) => {
        const icons: IconConfig[] = [];
        if (row.visibility === TaskVisibilityValues.PRIVATE) {
          icons.push({
            name: "lock" as const,
            size: 20,
            tooltip: {
              supportingText: "Task is Private"
            }
          });
        }

        if (!commentsEnabled) return icons;
        const count = row.commentsCount ?? 0;
        if (count === 0) return icons;
        icons.push({
          name: "mode_comment",
          size: 20,
          tooltip: {
            supportingText: `${count} ${count === 1 ? "comment" : "comments"}`
          }
        });
        return icons;
      }
    }),
    {
      id: "assignedToName",
      header: isCompactLayout ? "" : "Assignee",
      size: 200,
      enableSorting: true,
      meta: { sortType: "text" as const, sortLabel: "Assignee" },
      accessorFn: (row) => row.assignedTo?.name || "",
      cell: ({ row }) => {
        const assignee = row.original.assignedTo;
        if (!assignee) {
          return renderEmptyCellState(null);
        }

        return (
          <AvatarDetails
            picture={assignee.photo ?? undefined}
            title={assignee.name || "--"}
            text={assignee.email || <span className="opacity-20">--</span>}
          />
        );
      }
    },
    buildExpandableCountColumn<TaskNode>({
      accessorKey: "references",
      header: "References",
      getCount: (row) => row.references?.length || 0,
      size: 110
    }),
    {
      accessorFn: (row) => row.dueDate,
      id: "dueDate",
      header: "Due Date",
      enableSorting: true,
      meta: { sortType: "date" as const },
      cell: ({ getValue }) => renderEmptyCellState(toFormat(getValue() as string)),
      size: 140
    },
    stageColumn
  ];
}

export default function TasksTable({
  referenceableId,
  referenceableType,
  tableHeading = "Tasks",
  tableBody = "View and manage tasks for this item",
  hideControls = false,
  defaultPrivate = false,
  skipTeamAutoTag = false
}: TasksTableProps) {
  const { Menu, setMenu } = useTableRowMenu();
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const canManageTasks = useCanManageTasksBool();
  const currentUser = useCurrentBackofficeUser();
  const { isCompactLayout } = useIsCompactLayout();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<TaskNode | null>(null);
  const [addTaskOpen, setAddTaskOpen] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [stageFilter, setStageFilter] = useState<TaskStage | undefined>(undefined);
  const [sortingState, setSortingState] = useState<SortingState>([{ id: "dueDate", desc: false }]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchValue), 300);
    return () => clearTimeout(handler);
  }, [searchValue]);

  const orderBy = useMemo(() => {
    if (sortingState.length === 0) return undefined;
    const { id, desc } = sortingState[0];
    return {
      field: snakeCase(id).toUpperCase(),
      direction: (desc ? "DESC" : "ASC") as "ASC" | "DESC"
    };
  }, [sortingState]);

  const input = useMemo(() => {
    const base: TasksQueryVariables["input"] = {
      referenceableId,
      referenceableType: [referenceableType]
    };
    if (debouncedSearch.trim()) base.search = debouncedSearch.trim();
    if (stageFilter) base.stage = [stageFilter];
    return base;
  }, [referenceableId, referenceableType, debouncedSearch, stageFilter]);

  const prevInputRef = useRef(input);
  const prevOrderRef = useRef(orderBy);

  useEffect(() => {
    const inputChanged = prevInputRef.current !== input;
    const orderChanged = prevOrderRef.current !== orderBy;
    if (inputChanged || orderChanged) resetPagination();
    prevInputRef.current = input;
    prevOrderRef.current = orderBy;
  }, [input, orderBy, resetPagination]);

  const { data, loading, refetch } = useQuery<TasksQueryResponse, TasksQueryVariables>(TASKS_QUERY, {
    variables: {
      ...(hideControls ? {} : paginationVariables),
      input,
      orderBy
    },
    fetchPolicy: "cache-and-network"
  });

  const tasks = (data?.tasks?.nodes || []).filter((node): node is TaskNode => node != null);

  const { highlightRowId, containerRef } = useRowHighlight({ dataReady: !loading && tasks.length > 0 });

  const handleCloseSideSheet = () => {
    setIsSideSheetOpen(false);
    setSelectedTaskId(null);
  };

  const trackFilterChange = useCallback(
    (filter: string, value: string | boolean | undefined) => {
      trackEvent(TASK_ANALYTICS_EVENTS.listFiltered, {
        source: "embedded_tasks_table",
        referenceableType,
        filter,
        value
      });
    },
    [referenceableType, trackEvent]
  );

  const previousSearchRef = useRef("");
  useEffect(() => {
    const previousSearch = previousSearchRef.current.trim();
    const currentSearch = debouncedSearch.trim();
    if (currentSearch !== previousSearch) {
      trackFilterChange("search", Boolean(currentSearch));
      previousSearchRef.current = debouncedSearch;
    }
  }, [debouncedSearch, trackFilterChange]);

  const handleOpenTask = useCallback(
    (task: TaskNode, openSource: TaskOpenSource) => {
      trackEvent(TASK_ANALYTICS_EVENTS.opened, {
        ...getTaskAnalyticsData(task, "embedded_tasks_table"),
        referenceableType,
        openSource
      });
    },
    [referenceableType, trackEvent]
  );

  const defaultReferences: BackofficeCRMReferenceableInput[] = useMemo(
    () => [{ referenceableId, referenceableType }],
    [referenceableId, referenceableType]
  );

  const columns = useMemo(() => {
    const cols = buildDataColumns(isCompactLayout, true, handleOpenTask);
    cols.push(
      buildActionColumn<TaskNode>({
        getActions: (task: TaskNode) => {
          const actions: Action[] = [
            {
              text: "View Task",
              onClick: () => {
                handleOpenTask(task, "row_action");
                navigate(`/tasks/${task.id}` as Path);
              }
            }
          ];
          if (canManageTasks) {
            actions.push({
              text: "Delete Task",
              iconName: "delete",
              destructive: true,
              onClick: () => setTaskToDelete(task)
            });
          }
          return actions;
        },
        onViewClick: (task: TaskNode) => {
          handleOpenTask(task, "view_button");
          setSelectedTaskId(task.id);
          setIsSideSheetOpen(true);
        },
        showViewButton: () => true,
        viewButtonTitle: "View Task Details",
        actionId,
        setMenu
      })
    );
    return cols;
  }, [setMenu, canManageTasks, isCompactLayout, handleOpenTask, navigate]);

  const hasActiveFilters = !!searchValue || !!stageFilter;

  const handleClearAllFilters = () => {
    setSearchValue("");
    setStageFilter(undefined);
  };

  const actions: TableAction[] = canManageTasks
    ? [{ label: "Add Task", icon: "add", variant: "outlined", onClick: () => setAddTaskOpen(true) }]
    : [];

  const handleSortingChange = (updaterOrValue: SortingState | ((prev: SortingState) => SortingState)) => {
    const sortState = typeof updaterOrValue === "function" ? updaterOrValue(sortingState) : updaterOrValue;
    setSortingState(sortState);
  };

  return (
    <div ref={containerRef}>
      <Menu />
      <TableHeading heading={tableHeading} body={tableBody} actions={actions} />
      <Table
        data={tasks}
        loading={loading}
        columns={columns}
        onClickRow={(row) => {
          handleOpenTask(row.original, "row");
          navigate(`/tasks/${row.original.id}` as Path);
        }}
        hasActiveFilters={hasActiveFilters}
        serverSideSorting
        sortingState={sortingState}
        onSortingChange={handleSortingChange}
        getRowClasses={
          highlightRowId ? (row) => (row.original.id === highlightRowId ? "row-highlight" : "") : undefined
        }
        getRowCanExpand={(row) => (row.original.references?.length || 0) > 0}
        renderExpandedRow={({ row }) => (
          <ReferencesTable
            variant="details-panel"
            references={row.original.references || []}
            title={`References (${row.original.references?.length || 0})`}
            getParentLink={getReferenceParentLink}
          />
        )}
        renderControls={
          hideControls
            ? undefined
            : (_table, { onSortClick } = {}) => (
                <TableControls
                  pagination={createHeaderPagination(data?.tasks)}
                  onSortClick={onSortClick}
                  useExpandedFilters
                  expandedFiltersState={{
                    search: !!searchValue,
                    stage: !!stageFilter
                  }}
                  onClearAllFilters={handleClearAllFilters}
                  activeFilters={[!!searchValue, !!stageFilter].filter(Boolean).length}
                >
                  <SearchInput
                    data-name="search"
                    placeholder="Search Tasks"
                    query={searchValue}
                    onChange={setSearchValue}
                    onClear={() => setSearchValue("")}
                  />
                  <div data-name="stage" className="w-full sm:min-w-[160px] sm:max-w-[200px]">
                    <Select
                      label="Stage"
                      value={stageFilter || "ALL"}
                      dropdownHeight="full"
                      onSelect={(value) => {
                        const nextValue = value === "ALL" ? undefined : (value as TaskStage);
                        trackFilterChange("stage", nextValue);
                        setStageFilter(nextValue);
                      }}
                      options={[
                        { value: "ALL", displayText: "Show All", border: true },
                        { value: TaskStageValues.OPEN, displayText: "Open" },
                        { value: TaskStageValues.COMPLETE, displayText: "Complete" },
                        {
                          value: TaskStageValues.NOT_DOING,
                          displayText: "Not Doing"
                        }
                      ]}
                    />
                  </div>
                </TableControls>
              )
        }
        renderNoDataEmptyState={() => (
          <EmptyState
            iconName="assignment_turned_in"
            title="No Tasks Yet"
            caption="There are no tasks to display at this time."
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="search" />
        )}
        renderFooter={
          hideControls
            ? () => <TableFooter totalRowCount={data?.tasks?.totalCount ?? tasks.length} />
            : () => <TableFooter pagination={createFooterPagination(data?.tasks)} />
        }
      />
      <TaskSideSheet open={isSideSheetOpen} onClose={handleCloseSideSheet} taskId={selectedTaskId} />
      <DeleteTaskDialog
        open={!!taskToDelete}
        taskId={taskToDelete?.id ?? ""}
        task={taskToDelete ?? undefined}
        source="embedded_tasks_table"
        onClose={() => setTaskToDelete(null)}
        onDeleted={() => {
          setTaskToDelete(null);
          refetch();
        }}
      />
      {canManageTasks && (
        <AddTaskSideSheet
          open={addTaskOpen}
          onClose={() => setAddTaskOpen(false)}
          currentUser={currentUser}
          defaultReferences={defaultReferences}
          defaultPrivate={defaultPrivate}
          skipTeamAutoTag={skipTeamAutoTag}
          onSaveSuccess={() => refetch()}
          source="embedded_tasks_table"
        />
      )}
    </div>
  );
}
